import { useEffect, useState } from "react";
import { PASSENGER, TRIP_TYPE as TRIP } from "src/constants";
import { useDeviceContext } from "src/context/device-context";
import { DesktopSearchForm } from "./DesktopSearchForm";
import { MobileSearchForm } from "./MobileSearchForm";
import classes from "./SearchForm.module.css";

import { IconBeach } from "@tabler/icons-react";
import isEqual from "lodash.isequal";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useLocaleContext } from "src/context/locale-context";
import { hotelFilterActions } from "src/store/filter-sort";
import { searchDataActions } from "src/store/search";
import { buildSearchQuery } from "src/utils/query-utils";
import { getSelectedLocale, saveTripSearchToStorage } from "src/utils/storage-utils";

function HotelSearchForm() {
  const { getStringRes } = useLocaleContext();
  const location = useLocation();
  const isResultsPage = location.pathname.startsWith("/search/hotels");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchData = useSelector((state) => state.search, shallowEqual);

  const [prevSearchParams, setPrevSearchParams] = useState({
    locations: searchData.trips.map((tr) => [tr.srcLocation.iata, tr.destLocation.iata]),
    tripType: searchData.tripType,
  });
  const { isMobile } = useDeviceContext();

  const validateForm = (tripType, searchData, onValidateFailCallback) => {
    return validateTrip(tripType, searchData.trips[0], onValidateFailCallback);
  };

  useEffect(() => {
    for (let i = 0; i < 2 - searchData.passengers[PASSENGER.adult]; i++) {
      dispatch(searchDataActions.addPassenger(PASSENGER.adult));
    }
  }, []);

  useEffect(() => {
    if (searchData.tripType !== TRIP.roundtrip) {
      dispatch(searchDataActions.updateTripType(TRIP.roundtrip));
    }
  }, [dispatch, searchData.tripType]);

  const validateTrip = (tripType, trip, onValidateFailCallback) => {
    let screenId = "";
    if (tripType !== TRIP.roundtrip) {
      return false;
    } else if (!trip.srcLocation.city) {
      screenId = "src-location";
    } else if (!trip.destLocation.city) {
      screenId = "dest-location";
    } else if (!trip.startDate) {
      screenId = "start-date";
    } else if (!trip.endDate) {
      screenId = "end-date";
    } else {
      return true;
    }
    onValidateFailCallback(screenId, trip.id);
    return false;
  };

  const cacheSearchData = async (tripType, searchData) => {
    console.log("saving trip");
    searchData.trips.forEach((trip) => {
      saveTripSearchToStorage(trip, tripType);
    });
  };

  const preValidateForm = (tripType, searchData, onValidateFail) => {
    return validateForm(tripType, searchData, onValidateFail ? onValidateFail : () => {});
  };

  const onClickSearchHandler = (tripType, searchData, onValidateFail) => {
    if (validateForm(tripType, searchData, onValidateFail)) {
      const tripLocations = searchData.trips.map((tr) => [
        tr.srcLocation.iata,
        tr.destLocation.iata,
      ]);

      if (
        (prevSearchParams.locations && !isEqual(tripLocations, prevSearchParams.locations)) ||
        prevSearchParams.tripType !== tripType
      ) {
        dispatch(hotelFilterActions.clearAllFilters());
      }
      setPrevSearchParams({ locations: tripLocations, tripType: tripType });
      cacheSearchData(tripType, searchData);

      const q = buildSearchQuery(searchData);
      const locale = getSelectedLocale();
      const lang = locale.split("_")[0];

      navigate(`/search/hotels?lang=${lang}&${q}&includeHotels=true`, { replace: isResultsPage });
      return true;
    }
    return false;
  };

  const props = {
    onSearchStart: onClickSearchHandler,
    isResultsPage: isResultsPage,
    hideTripTypes: true,
    hideCheckboxes: true,
    searchType: "hotels",
    hideCabinOptions: true,
    heading: (
      <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
        <IconBeach size={40} />
        {getStringRes("sf.desktop.title.hotels")}
      </span>
    ),
  };

  if (isResultsPage && !isMobile) {
    return <DesktopSearchForm key="desktop-results" {...props} />;
  } else {
    return (
      <section
        id={isMobile ? "search-form-section-mob" : "search-form-section"}
        className={`${classes.container} ${classes.search} ${
          isMobile ? "mobile" : classes.desktop
        } no-img`}>
        {isMobile ? (
          <MobileSearchForm
            key={isResultsPage ? "mobile-results" : "mobile-homepage"}
            preValidate={preValidateForm}
            {...props}
          />
        ) : (
          <DesktopSearchForm key="desktop-homepage" {...props} />
        )}
      </section>
    );
  }
}

export default HotelSearchForm;
